<template>
  <div class="resource-management">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>资源管理</span>
        <div style="float: right;">
          <el-button style="padding: 3px 10px" type="text" @click="handleAdd">新增资源</el-button>
          <el-button style="padding: 3px 10px" type="text" @click="goToBatchUpload">批量新增</el-button>
        </div>
      </div>

      <!-- Category Selector -->
      <div class="category-selector">
        <el-select 
          v-model="selectedCategoryId" 
          placeholder="请选择三级分类（不选择则显示全部）" 
          clearable
          @change="loadResources">
          <el-option
            v-for="category in level3Categories"
            :key="category.id"
            :label="category.title"
            :value="category.id">
          </el-option>
        </el-select>
        <el-button @click="loadLevel3Categories" style="margin-left: 10px;">刷新分类</el-button>
      </div>

      <!-- Resource Table -->
      <el-table
        :data="resources"
        style="width: 100%"
        v-loading="loading">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column label="媒体" width="120">
          <template slot-scope="scope">
            <div class="media-preview">
              <img 
                v-if="scope.row.mediaType === 1" 
                :src="scope.row.mediaUrl" 
                class="media-img"
                @click="previewMedia(scope.row.mediaUrl, 1)">
              <video 
                v-else 
                :src="scope.row.mediaUrl" 
                class="media-video"
                @click="previewMedia(scope.row.mediaUrl, 2)">
              </video>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="媒体类型" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.mediaType === 1 ? 'success' : 'warning'">
              {{ scope.row.mediaType === 1 ? '图片' : '视频' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="80"></el-table-column>
        <el-table-column label="操作" width="150">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
              <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- Empty State -->
      <div v-if="!loading && resources.length === 0" class="empty-state">
        <el-empty description="暂无资源数据">
          <el-button type="primary" @click="handleAdd" v-if="selectedCategoryId">添加资源</el-button>
        </el-empty>
      </div>
    </el-card>

    <!-- Add/Edit Dialog -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="所属分类" prop="category_id">
          <el-select v-model="form.category_id" placeholder="请选择三级分类" style="width: 100%">
            <el-option
              v-for="category in level3Categories"
              :key="category.id"
              :label="category.title"
              :value="category.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="媒体类型" prop="media_type">
          <el-radio-group v-model="form.media_type">
            <el-radio :label="1">图片</el-radio>
            <el-radio :label="2">视频</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="媒体链接" prop="media_url">
          <el-input 
            v-model="form.media_url" 
            placeholder="可以直接填写图片或视频的链接地址（支持 http/https 链接）"
            style="margin-bottom: 10px;">
            <template slot="prepend">
              <i class="el-icon-link"></i>
            </template>
          </el-input>
          <div class="upload-divider">
            <span class="divider-text">或</span>
          </div>
          <el-upload
            action="#"
            :http-request="handleCustomUpload"
            :before-upload="beforeUpload"
            :show-file-list="false">
            <el-button size="small" type="primary" :disabled="uploadStatus === 'uploading' || uploadStatus === 'processing'">点击上传文件</el-button>
            <div slot="tip" class="el-upload__tip">
              支持 jpg/png 图片或 mp4 视频（大视频建议直接填写链接，速度更快）
            </div>
          </el-upload>
          <!-- 上传进度显示 -->
          <div v-if="uploadStatus === 'uploading' || uploadStatus === 'processing'" class="upload-progress">
            <div class="progress-info">
              <span v-if="uploadStatus === 'uploading'">上传中: {{ uploadProgress }}%</span>
              <span v-else-if="uploadStatus === 'processing'">
                <i class="el-icon-loading"></i> 等待服务器处理中...
              </span>
            </div>
            <el-progress 
              v-if="uploadStatus === 'uploading'"
              :percentage="uploadProgress" 
              :status="uploadStatus === 'error' ? 'exception' : ''"
              :stroke-width="8">
            </el-progress>
            <el-progress 
              v-else-if="uploadStatus === 'processing'"
              :percentage="100" 
              status="success"
              :stroke-width="8">
            </el-progress>
          </div>
          <div v-if="form.media_url" class="uploaded-media">
            <img 
              v-if="form.media_type === 1" 
              :src="form.media_url" 
              class="preview-img"
              @error="handleMediaError">
            <video 
              v-else 
              :src="form.media_url" 
              class="preview-video"
              controls
              @error="handleMediaError">
            </video>
          </div>
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="form.sort" :min="0"></el-input-number>
          <div class="sort-tip">数字越小越靠前显示</div>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>

    <!-- Media Preview Dialog -->
    <el-dialog title="媒体预览" :visible.sync="previewVisible" width="800px">
      <div class="media-container">
        <img v-if="previewType === 1" :src="previewUrl" class="preview-full-img">
        <video v-else :src="previewUrl" class="preview-full-video" controls></video>
      </div>
    </el-dialog>

    <!-- Batch Upload Dialog -->
    <el-dialog title="批量上传资源" :visible.sync="batchUploadVisible" width="600px">
      <!-- 老的批量上传弹窗逻辑暂时保留，但不再使用，批量上传改为新页面 -->
      <div style="padding: 20px; text-align: center; color: #909399;">
        批量上传功能已迁移到独立页面，请通过上方“批量新增”按钮进入新页面操作。
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { categoryApi, resourceApi, uploadApi } from '@/api'

export default {
  name: 'ResourceManagement',
  data() {
    return {
      resources: [],
      level3Categories: [],
      selectedCategoryId: null,
      loading: false,
      dialogVisible: false,
      batchUploadVisible: false,
      previewVisible: false,
      dialogTitle: '新增资源',
      previewUrl: '',
      previewType: 1,
      batchFileList: [],
      uploadProgress: 0,
      uploadStatus: '', // 'uploading' | 'processing' | 'success' | 'error' | ''
      batchUploadProgress: {}, // 批量上传进度 { fileName: { progress, status } }
      form: {
        id: null,
        category_id: null,
        media_url: '',
        media_type: 1,
        sort: 0
      },
      batchForm: {
        category_id: null
      },
      rules: {
        category_id: [
          { required: true, message: '请选择所属分类', trigger: 'change' }
        ],
        media_type: [
          { required: true, message: '请选择媒体类型', trigger: 'change' }
        ],
        media_url: [
          { required: true, message: '请上传媒体文件或填写媒体链接', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.loadLevel3Categories()
    // 首次进入时加载全部资源
    this.loadResources()
  },
  methods: {
    async loadLevel3Categories() {
      try {
        const response = await categoryApi.getAll({ level: 3 })
        this.level3Categories = Array.isArray(response) ? response.sort((a, b) => a.sort - b.sort) : []
      } catch (error) {
        this.$message.error('加载三级分类失败: ' + error.message)
      }
    },
    async loadResources() {
      this.loading = true
      try {
        let response
        if (this.selectedCategoryId) {
          // 如果选择了分类，则按分类查询
          response = await resourceApi.getByCategory(this.selectedCategoryId)
        } else {
          // 如果没有选择分类，则全量查询
          response = await resourceApi.getAll()
        }
        this.resources = Array.isArray(response) ? response.sort((a, b) => a.sort - b.sort) : []
      } catch (error) {
        this.$message.error('加载资源失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },
    handleAdd() {
      if (!this.selectedCategoryId) {
        this.$message.warning('请先选择一个三级分类')
        return
      }
      
      this.dialogTitle = '新增资源'
      this.form = {
        id: null,
        category_id: this.selectedCategoryId,
        media_url: '',
        media_type: 1,
        sort: this.resources.length > 0 ? Math.max(...this.resources.map(r => r.sort)) + 1 : 0
      }
      // 重置上传状态
      this.uploadProgress = 0
      this.uploadStatus = ''
      this.dialogVisible = true
    },
    handleEdit(row) {
      this.dialogTitle = '编辑资源'
      this.form = {
        id: row.id,
        category_id: row.categoryId || row.category_id,
        media_url: row.mediaUrl || row.media_url,
        media_type: row.mediaType || row.media_type,
        sort: row.sort
      }
      // 重置上传状态
      this.uploadProgress = 0
      this.uploadStatus = ''
      this.dialogVisible = true
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除这个资源吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await resourceApi.delete(row.id)
        this.$message.success('删除成功')
        this.loadResources()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败: ' + error.message)
        }
      }
    },
    async handleSubmit() {
      try {
        await this.$refs.form.validate()
        // 将表单的下划线格式转换为接口需要的驼峰格式
        const submitData = {
          categoryId: this.form.category_id,
          mediaUrl: this.form.media_url,
          mediaType: this.form.media_type,
          sort: this.form.sort
        }
        if (this.form.id) {
          await resourceApi.update(this.form.id, submitData)
          this.$message.success('更新成功')
        } else {
          await resourceApi.create(submitData)
          this.$message.success('创建成功')
        }
        this.dialogVisible = false
        this.loadResources()
      } catch (error) {
        this.$message.error('操作失败: ' + error.message)
      }
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')
      const isValidType = isImage || isVideo

      if (!isValidType) {
        this.$message.error('只能上传图片或视频文件!')
        return false
      }

      // 不自动修改媒体类型，交给用户通过单选框手动选择
      return true
    },
    async handleCustomUpload(options) {
      // 重置上传状态
      this.uploadProgress = 0
      this.uploadStatus = 'uploading'
      
      try {
        const response = await uploadApi.uploadFile(options.file, (progress) => {
          // 更新上传进度
          this.uploadProgress = progress
          if (progress >= 100) {
            // 上传完成，等待服务器处理
            this.uploadStatus = 'processing'
          }
        })
        
        // uploadApi 返回的是经过拦截器处理后的 data.data
        // 如果后端返回 { code: 200, data: "图片URL" }，这里 response 就是 "图片URL"
        // 如果后端返回 { code: 200, data: { url: "图片URL" } }，这里 response 就是 { url: "图片URL" }
        if (typeof response === 'string') {
          this.form.media_url = response
        } else if (response && response.url) {
          this.form.media_url = response.url
        } else if (response && response.data) {
          this.form.media_url = typeof response.data === 'string' ? response.data : response.data.url
        } else {
          this.form.media_url = response
        }
        
        // 上传成功
        this.uploadStatus = 'success'
        this.$message.success('上传成功')
        
        // 延迟清除状态，让用户看到成功提示
        setTimeout(() => {
          this.uploadStatus = ''
          this.uploadProgress = 0
        }, 2000)
      } catch (error) {
        this.uploadStatus = 'error'
        this.$message.error('上传失败: ' + (error.message || '未知错误'))
        
        // 延迟清除错误状态
        setTimeout(() => {
          this.uploadStatus = ''
          this.uploadProgress = 0
        }, 3000)
      }
    },
    previewMedia(url, type) {
      this.previewUrl = url
      this.previewType = type
      this.previewVisible = true
    },
    handleMediaError() {
      // 媒体加载失败时的处理（静默处理，不显示错误）
      // 可能是链接无效，但不影响用户继续操作
    },
    goToBatchUpload() {
      this.$router.push('/resources/batch-upload')
    },
    async handleBatchUpload() {
      if (!this.batchForm.category_id) {
        this.$message.error('请选择所属分类')
        return
      }
      
      if (this.batchFileList.length === 0) {
        this.$message.error('请选择要上传的文件')
        return
      }

      try {
        // 构造一次性提交的资源数组
        const resourcesToCreate = this.batchFileList
          .map(file => {
            const mediaType = file.raw.type.startsWith('image/') ? 1 : 2
            // file.url 在 handleBatchUploadSuccess/handleBatchCustomUpload 中已设置为上传后的 URL
            const mediaUrl = file.url || (file.response && file.response.code === 200 ? file.response.data : null)

            if (!mediaUrl) {
              this.$message.error(`文件 ${file.name} 上传失败，请重新上传`)
              return null
            }

            return {
              categoryId: this.batchForm.category_id,
              mediaUrl: mediaUrl,
              mediaType: mediaType,
              // 批量新增时排序默认都为 0，后续再手动调整
              sort: 0
            }
          })
          .filter(item => item !== null)

        if (resourcesToCreate.length === 0) {
          this.$message.error('没有可用的资源可以提交，请检查上传结果')
          return
        }

        // 一次性按数组提交，后端使用 @RequestBody List<CategoryResource>
        await resourceApi.create(resourcesToCreate)

        this.$message.success('批量上传成功')
        this.batchUploadVisible = false
        this.loadResources()
      } catch (error) {
        this.$message.error('批量上传失败: ' + error.message)
      }
    },
    async handleBatchCustomUpload(options) {
      const fileName = options.file.name
      
      // 初始化该文件的上传进度
      this.$set(this.batchUploadProgress, fileName, {
        progress: 0,
        status: 'uploading'
      })
      
      try {
        const response = await uploadApi.uploadFile(options.file, (progress) => {
          // 更新该文件的上传进度
          this.$set(this.batchUploadProgress, fileName, {
            progress: progress,
            status: progress >= 100 ? 'processing' : 'uploading'
          })
        })
        
        // uploadApi 返回的是经过拦截器处理后的 data.data
        // 如果后端返回 { code: 200, data: "图片URL" }，这里 response 就是 "图片URL"
        // 如果后端返回 { code: 200, data: { url: "图片URL" } }，这里 response 就是 { url: "图片URL" }
        let fileUrl
        if (typeof response === 'string') {
          fileUrl = response
        } else if (response && response.url) {
          fileUrl = response.url
        } else if (response && response.data) {
          fileUrl = typeof response.data === 'string' ? response.data : response.data.url
        } else {
          fileUrl = response
        }
        
        // 标记为上传成功
        this.$set(this.batchUploadProgress, fileName, {
          progress: 100,
          status: 'success'
        })
        
        // 将上传成功的 URL 保存到 file 对象中
        options.onSuccess({ code: 200, data: fileUrl }, options.file)
        
        // 3秒后清除该文件的进度显示
        setTimeout(() => {
          this.$delete(this.batchUploadProgress, fileName)
        }, 3000)
      } catch (error) {
        // 标记为上传失败（仅在真正请求失败时使用）
        this.$set(this.batchUploadProgress, fileName, {
          progress: 100,
          status: 'error'
        })

        options.onError && options.onError(error)
        // 实际上上传已经成功但被错误拦截的情况，这里不要再给用户弹“未知错误”的提示，避免干扰
        // 只在控制台输出，方便排查
        // eslint-disable-next-line no-console
        console.error(`文件 ${fileName} 上传过程中出现异常:`, error)

        // 5秒后清除该文件的进度显示
        setTimeout(() => {
          this.$delete(this.batchUploadProgress, fileName)
        }, 5000)
      }
    },
    handleBatchUploadSuccess(response, file, fileList) {
      // 处理批量上传成功后的回调
      if (response && response.code === 200) {
        file.url = response.data
      } else {
        this.$message.error(`文件 ${file.name} 上传失败: ${response?.message || '未知错误'}`)
      }
      this.batchFileList = fileList
    }
  }
}
</script>

<style scoped>
.resource-management {
  padding: 20px;
}

.category-selector {
  margin: 20px 0;
  display: flex;
  align-items: center;
}

.media-preview {
  width: 60px;
  height: 60px;
  cursor: pointer;
}

.media-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.media-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.uploaded-media {
  margin-top: 10px;
}

.preview-img {
  width: 200px;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
}

.preview-video {
  width: 200px;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
}

.media-container {
  text-align: center;
}

.preview-full-img {
  max-width: 100%;
  max-height: 500px;
}

.preview-full-video {
  max-width: 100%;
  max-height: 500px;
}

.empty-state {
  margin-top: 50px;
}

.sort-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 5px;
}

.action-buttons {
  display: flex;
  flex-wrap: nowrap;
  gap: 5px;
  align-items: center;
}

.action-buttons .el-button {
  margin: 0;
  padding: 7px 12px;
}

.upload-divider {
  margin: 15px 0;
  text-align: center;
  position: relative;
}

.upload-divider::before,
.upload-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background-color: #dcdfe6;
}

.upload-divider::before {
  left: 0;
}

.upload-divider::after {
  right: 0;
}

.divider-text {
  position: relative;
  padding: 0 10px;
  background-color: #fff;
  color: #909399;
  font-size: 12px;
}

.upload-progress {
  margin-top: 10px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.progress-info {
  margin-bottom: 8px;
  font-size: 14px;
  color: #606266;
  display: flex;
  align-items: center;
  gap: 5px;
}

.progress-info .el-icon-loading {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.batch-upload-progress {
  margin-top: 15px;
  max-height: 300px;
  overflow-y: auto;
}

.batch-progress-item {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.progress-file-name {
  font-size: 13px;
  color: #303133;
  margin-bottom: 8px;
  font-weight: 500;
}

.batch-progress-item .progress-info {
  margin-bottom: 8px;
  font-size: 13px;
}

.success-text {
  color: #67c23a;
}

.error-text {
  color: #f56c6c;
}
</style>
